import {
  DataTable,
  EmptyState,
  ErrorState,
  Loading,
  formatDate,
  formatHours,
  formatMoney,
} from "@corecrm/ui";
import { useQueries } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { keys, useProjects } from "../api/queries";
import type { Budget, Project, ProjectSummary } from "../api/types";
import { HoursBar } from "../components/bits";
import { BILLING_MODEL, BILLING_ONLY_NOTE } from "../lib/labels";

/* Hours and budget for every project in one table, the way each is billed: hours used against the
   budget (fixed fee, with its phases under it), billable hours and the next invoice to date
   (time and materials), or this month's balance (maintenance). A column shows only when some
   project has something for it. Totals are per currency: CAD and USD are never added together.
   Hosting projects are left out (billed at a flat monthly rate). */

type Money = { amount_minor: number; currency: string };

interface Line {
  /** A project row, or one of its fixed-fee phases (indented), or a total. */
  kind: "project" | "phase" | "total";
  id: string;
  name: string;
  link?: string;
  billingType?: string;
  hoursUsed?: number;
  hoursBudget?: number | null;
  /** Hours used on the phases that have a budget (the part "of" the budget speaks about). */
  budgetedUsed?: number;
  billableHours?: number;
  /** What the next invoice stands at, to date. */
  next?: Money;
  details?: React.ReactNode;
  failed?: boolean;
}

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);

/** One project's lines: the project, then (fixed fee) its phases. */
export function projectLines(summary: ProjectSummary, project?: Project, budget?: Budget): Line[] {
  const base: Line = {
    kind: "project",
    id: summary.id,
    name: summary.name,
    link: `/projects/${summary.id}`,
    billingType: BILLING_MODEL[summary.billing_model] ?? summary.billing_model,
  };
  if (!project || !budget) return [{ ...base, failed: true }];
  const billing = project.billing_visible;
  if (budget.maintenance) {
    const m = budget.maintenance;
    return [
      {
        ...base,
        next: m.over,
        details: (
          <span className="stack">
            <span className="faint">
              {formatDate(m.start_date)} – {formatDate(m.end_date)}
            </span>
            <span className="mono">
              Monthly balance {formatMoney(m.monthly_balance)} · Rolled over {formatMoney(m.rolled_over)} ·
              Used {formatMoney(m.used)} · Remaining {formatMoney(m.remaining)}
            </span>
            {m.over.amount_minor > 0 && (
              <span className="mono">Over balance {formatMoney(m.over)} (invoiced at month end)</span>
            )}
            <span className="faint">
              Unused balance rolls over for two more months, then expires (the oldest is used first).
            </span>
          </span>
        ),
      },
    ];
  }
  if (budget.billing_model === "maintenance")
    return [
      {
        ...base,
        details: (
          <span className="muted">
            {billing ? "No maintenance month is open right now." : BILLING_ONLY_NOTE}
          </span>
        ),
      },
    ];
  if (budget.billing_model === "time_and_materials")
    return [
      {
        ...base,
        billableHours: Number(budget.billable_hours ?? 0),
        next: billing && budget.billable_amount ? budget.billable_amount : undefined,
        details: billing ? (
          <span className="mono">{budget.cap ? `Cap ${formatMoney(budget.cap)}` : "No cap"}</span>
        ) : null,
      },
    ];
  // Fixed fee: the project's hours, then each phase's.
  const used = sum(budget.phases.map((x) => Number(x.hours_used)));
  const total = sum(budget.phases.map((x) => Number(x.budget_hours ?? 0)));
  const budgetedUsed = sum(budget.phases.filter((x) => x.budget_hours).map((x) => Number(x.hours_used)));
  return [
    {
      ...base,
      hoursUsed: used,
      hoursBudget: total || null,
      budgetedUsed,
      details: project.budget_amount ? (
        <span className="mono">Fixed fee {formatMoney(project.budget_amount)} + tax</span>
      ) : null,
    },
    ...budget.phases.map((ph): Line => ({
      kind: "phase",
      id: `${summary.id}-${ph.phase_id}`,
      name: ph.name,
      hoursUsed: Number(ph.hours_used),
      hoursBudget: ph.budget_hours ? Number(ph.budget_hours) : null,
    })),
  ];
}

/** The total lines: hours, billable hours and the money per currency. One line when there is a
    single currency; with more, a "Total" for the hours then one line per currency. */
export function totalLines(lines: Line[]): Line[] {
  const projects = lines.filter((l) => l.kind === "project");
  const hoursUsed = sum(projects.map((l) => l.hoursUsed ?? 0));
  const budgets = projects.filter((l) => l.hoursUsed !== undefined);
  const hoursBudget = sum(budgets.map((l) => l.hoursBudget ?? 0));
  const budgetedUsed = sum(budgets.map((l) => l.budgetedUsed ?? 0));
  const billable = sum(projects.map((l) => l.billableHours ?? 0));
  const hours: Partial<Line> = {
    hoursUsed: budgets.length ? hoursUsed : undefined,
    hoursBudget: budgets.length ? hoursBudget || null : undefined,
    budgetedUsed: budgets.length ? budgetedUsed : undefined,
    billableHours: projects.some((l) => l.billableHours !== undefined) ? billable : undefined,
  };
  const money = new Map<string, number>();
  for (const l of projects)
    if (l.next) money.set(l.next.currency, (money.get(l.next.currency) ?? 0) + l.next.amount_minor);
  const currencies = [...money.entries()].sort(([a], [b]) => a.localeCompare(b));
  const total = (id: string, name: string, extra: Partial<Line>): Line => ({
    kind: "total",
    id,
    name,
    ...extra,
  });
  if (currencies.length <= 1) {
    const [only] = currencies;
    return [
      total("total", "Total", {
        ...hours,
        next: only ? { currency: only[0], amount_minor: only[1] } : undefined,
      }),
    ];
  }
  return [
    total("total", "Total", hours),
    ...currencies.map(([currency, amount_minor]) =>
      total(`total-${currency}`, `Total (${currency})`, { next: { currency, amount_minor } }),
    ),
  ];
}

export function BudgetPage() {
  const projects = useProjects();
  const shown = (projects.data ?? []).filter((p) => p.billing_model !== "hosting");
  // Each project's detail and budget, in one go (the same queries the project page uses).
  const details = useQueries({
    queries: shown.flatMap((p) => [
      { queryKey: keys.project(p.id), queryFn: () => api.get<Project>(`/projects/${p.id}`) },
      { queryKey: keys.budget(p.id), queryFn: () => api.get<Budget>(`/projects/${p.id}/budget`) },
    ]),
  });
  if (projects.isPending) return <Loading />;
  if (projects.error) return <ErrorState error={projects.error} onRetry={() => projects.refetch()} />;
  if (!shown.length) return <EmptyState>No projects yet.</EmptyState>;
  if (details.some((d) => d.isPending)) return <Loading />;
  const lines = shown.flatMap((p, i) =>
    projectLines(
      p,
      details[i * 2]?.data as Project | undefined,
      details[i * 2 + 1]?.data as Budget | undefined,
    ),
  );
  return <BudgetTable lines={lines} />;
}

/** Hours were used on something with no budget, next to something that has one. */
const unbudgeted = (l: Line) =>
  !!l.hoursBudget && l.budgetedUsed !== undefined && l.hoursUsed! - l.budgetedUsed > 0.005;

export function BudgetTable({ lines }: { lines: Line[] }) {
  const totals = totalLines(lines);
  const all = [...lines, ...totals];
  const has = (f: (l: Line) => boolean) => lines.some(f);
  const rows: Line[] = [...lines, { kind: "total", id: "totals-heading", name: "" }, ...totals];
  return (
    <DataTable<Line>
      label="Budget and hours by project"
      className="budget-table"
      rows={rows}
      rowKey={(l) => `${l.kind}-${l.id}`}
      sectionRow={(l) => (l.id === "totals-heading" ? { level: 1, content: "Totals" } : null)}
      rowClassName={(l) => (l.kind === "total" ? "total-row" : l.kind === "phase" ? "phase-row" : null)}
      columns={[
        {
          header: "Project",
          cell: (l) =>
            l.kind === "phase" ? (
              <span style={{ paddingLeft: 20 }}>{l.name}</span>
            ) : l.kind === "total" ? (
              <strong>{l.name}</strong>
            ) : (
              <>
                <Link to={l.link!}>
                  <strong>{l.name}</strong>
                </Link>
                {l.failed && <span className="sub">Could not load this project&apos;s budget.</span>}
              </>
            ),
        },
        { header: "Billing type", cell: (l) => l.billingType ?? "" },
        {
          header: "Hours used / budget",
          hidden: !all.some((l) => l.hoursUsed !== undefined),
          cell: (l) =>
            l.hoursUsed === undefined ? null : unbudgeted(l) ? (
              // Some hours have no budget: "of" would compare hours with a budget that excludes them.
              <span className="mono">
                {formatHours(l.hoursUsed)} used · {formatHours(l.budgetedUsed)} of{" "}
                {formatHours(l.hoursBudget)} budgeted
              </span>
            ) : l.kind === "total" ? (
              <span className="mono">
                {formatHours(l.hoursUsed)}
                {l.hoursBudget ? ` of ${formatHours(l.hoursBudget)}` : ""}
              </span>
            ) : (
              <HoursBar used={l.hoursUsed} budget={l.hoursBudget ?? null} label={`${l.name} hours used`} />
            ),
        },
        {
          header: "Billable hours",
          align: "right",
          hidden: !has((l) => l.billableHours !== undefined),
          cell: (l) =>
            l.billableHours === undefined ? null : (
              <span className="mono">{formatHours(l.billableHours)}</span>
            ),
        },
        {
          header: "Next Invoice (To Date)",
          align: "right",
          hidden: !all.some((l) => l.next),
          cell: (l) => (l.next ? <span className="mono">{formatMoney(l.next)}</span> : null),
        },
        {
          header: "Details",
          role: "wide",
          hidden: !has((l) => !!l.details),
          cell: (l) => l.details ?? null,
        },
      ]}
    />
  );
}
